import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { wishScore } from "@/lib/journey";
import { Target, Loader2 } from "lucide-react";

const tone = (pct) => (pct >= 80 ? "bg-status-complete/15 text-status-complete" : pct >= 50 ? "bg-status-warning/15 text-status-warning" : "bg-secondary text-muted-foreground");

export function WishMatchBadge({ tour, profile, testId }) {
  const s = wishScore(tour, profile);
  if (!s) return null;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${tone(s.pct)}`} data-testid={testId} title={`Meets ${s.met} of ${s.total} of your wish list items`}>
      <Target className="h-3.5 w-3.5" /> {s.pct}% wish list match
    </span>
  );
}

export function WishMatchEditor({ tour, criteria, onSaved }) {
  const [sel, setSel] = useState(tour.wish_matches || []);
  const save = useMutation({
    mutationFn: async () => (await api.put(`/agent/tours/${tour.id}/wish-matches`, { matches: sel })).data,
    onSuccess: () => { onSaved?.(); toast.success("Wish list match saved"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  if (!criteria.length) return <p className="text-xs text-muted-foreground">Add must-haves to this client's profile first.</p>;
  const pct = Math.round((criteria.filter((c) => sel.includes(c)).length / criteria.length) * 100);
  const toggle = (c) => setSel(sel.includes(c) ? sel.filter((x) => x !== c) : [...sel, c]);
  return (
    <div data-testid={`wish-editor-${tour.id}`}>
      <p className="text-xs text-muted-foreground mb-2">Tick each item this home meets. Score: <span className="font-semibold text-foreground">{pct}%</span></p>
      <div className="grid sm:grid-cols-2 gap-1.5">
        {criteria.map((c, i) => (
          <label key={c} className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={sel.includes(c)} onChange={() => toggle(c)} className="h-4 w-4 accent-primary" data-testid={`wish-check-${tour.id}-${i}`} /> {c}
          </label>
        ))}
      </div>
      <Button size="sm" className="rounded-full mt-3" onClick={() => save.mutate()} disabled={save.isPending} data-testid={`wish-save-${tour.id}`}>
        {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save match score"}
      </Button>
    </div>
  );
}
